import { TaxStrategy, roundTo2Decimals } from '@principle-theorem/accounting';
import {
  ITranslationMap,
  type IGetRecordResponse,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Invoice,
  Transaction,
  TransactionOperators,
  toAccountDetails,
} from '@principle-theorem/principle-core';
import {
  IManualExtendedData,
  IManualTransactionType,
  IPatient,
  IStaffer,
  ITreatmentConfiguration,
  InvoiceLineItemType,
  InvoiceStatus,
  TransactionProvider,
  TransactionStatus,
  TransactionType,
  type IInvoice,
  type IPractice,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  INamedDocument,
  WithPrimaryKey,
  WithRef,
  resolveSequentially,
  sortByCreatedAt,
  sortTimestampAsc,
  toISODate,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
  type IIdentifiable,
} from '@principle-theorem/shared';
import { compact, first, groupBy, minBy, sumBy } from 'lodash';
import { ItemCodeResourceMapType } from '../../../../mappings/item-codes-to-xlsx';
import { TranslationMapHandler } from '../../../../translation-map';
import { type IExactV2InvoiceLine } from '../../../source/entities/patient-invoice-lines-v2';
import { ExactLineItems } from './exact-line-items';
import { ExactOpenCredit } from './exact-open-credit';
import {
  ExactSettlement,
  type IExactChargeAmount,
  type IExactPaymentSettlement,
} from './exact-settlement';
import { ExactTransactionLabels } from './exact-transaction-labels';
import { ExactTransactionLedger } from './exact-transaction-ledger';
import {
  ExactV2Transaction,
  type IExactV2Transaction,
} from '../../../source/entities/patient-transactions-v2';
import { type IExactV2TransactionAllocation } from '../../../source/entities/transaction-allocations-v2';
import { resolveExactStaffLocation } from '../../mappings/practitioner-to-practice-mapping';

export interface IInvoiceBuildData {
  invoice: IIdentifiable & IInvoice;
  transactions: (IIdentifiable & ITransaction)[];
}

const PENDING_CLAIM_UID_SUFFIX = '-claim';
const SETTLED_BALANCE_UID_SUFFIX = '-settled';

/**
 * Pending transactions don't reduce `Invoice.balance`, so this is the amount
 * by which built invoices intentionally exceed the patient's debtacc3 balance.
 */
export function getExactV2PendingClaimTotal(
  invoiceData: IInvoiceBuildData[]
): number {
  return roundTo2Decimals(
    sumBy(invoiceData, ({ transactions }) =>
      sumBy(
        transactions.filter(
          (transaction) => transaction.status === TransactionStatus.Pending
        ),
        (transaction) => transaction.amount
      )
    )
  );
}

/**
 * Document assembly only. `ExactTransactionLedger` says what each source row
 * is, `ExactSettlement` who paid what, `ExactOpenCredit` what is left over.
 */
export class ExactInvoiceBuilderV2 {
  private _practiceCache = new Map<string, WithRef<IPractice> | undefined>();
  private _ledger: ExactTransactionLedger;
  private _lineItems: ExactLineItems;
  private _settlement: ExactSettlement;
  private _openCredit: ExactOpenCredit;
  private _labels: ExactTransactionLabels;

  constructor(
    public sourceTransactions: IGetRecordResponse<ExactV2Transaction>[],
    public invoiceLines: IExactV2InvoiceLine[],
    public allocations: IExactV2TransactionAllocation[],
    public patientBalance: number,
    public staff: WithPrimaryKey<ITranslationMap<IStaffer, unknown>>[],
    public sourceItemCodes: WithPrimaryKey<
      ITranslationMap<object, ItemCodeResourceMapType>
    >[],
    public sourceTreatmentMappings: WithPrimaryKey<
      ITranslationMap<ITreatmentConfiguration>
    >[],
    public staffToPractice: WithPrimaryKey<ITranslationMap<IPractice>>[],
    public payorLabels: WithPrimaryKey<
      ITranslationMap<IManualTransactionType, string>
    >[],
    public paymentMethods: WithPrimaryKey<
      ITranslationMap<IManualTransactionType, string>
    >[]
  ) {
    const transactions = sourceTransactions.map((record) => record.data);
    this._ledger = new ExactTransactionLedger(
      transactions,
      invoiceLines,
      allocations
    );
    this._lineItems = new ExactLineItems(
      this._ledger,
      sourceItemCodes,
      sourceTreatmentMappings,
      staff
    );
    this._settlement = new ExactSettlement(this._ledger);
    this._openCredit = new ExactOpenCredit(transactions, patientBalance);
    this._labels = new ExactTransactionLabels(payorLabels, paymentMethods);
  }

  /**
   * Group by (course-of-treatment, date) so a multi-visit course splits into
   * one invoice per visit. Charges with no course fall back to date-only. Also
   * the invoice uid, so a re-run upserts the same invoice.
   */
  private static _invoiceKey(charge: IExactV2Transaction): string {
    return `${charge.courseOfTreatmentId ?? 'no-cot'}-${charge.date}`;
  }

  async buildInvoices(
    patient: WithRef<IPatient>,
    translationMap: TranslationMapHandler,
    migrationPractices: INamedDocument<IPractice>[]
  ): Promise<IInvoiceBuildData[]> {
    const { deposits } = this._ledger.categorise();

    const byInvoice = groupBy(this._ledger.billableCharges(), (charge) =>
      ExactInvoiceBuilderV2._invoiceKey(charge)
    );

    const invoices = await resolveSequentially(
      Object.values(byInvoice),
      async (invoiceCharges) => {
        return this._buildInvoiceFromCharges(
          invoiceCharges,
          patient,
          translationMap,
          migrationPractices
        );
      }
    );

    const depositInvoices =
      deposits.length > 0
        ? await this._buildDepositInvoices(
            patient,
            deposits,
            migrationPractices,
            translationMap
          )
        : [];

    const allInvoices = [...compact(invoices), ...depositInvoices].sort(
      (aInvoice, bInvoice) =>
        sortTimestampAsc(aInvoice.invoice.createdAt, bInvoice.invoice.createdAt)
    );

    this._openCredit.spend(allInvoices);

    return allInvoices.map((invoiceData) => {
      const status = this._getInvoiceStatus(
        Invoice.balance(invoiceData.invoice, invoiceData.transactions)
      );
      const lastTransaction = new TransactionOperators(invoiceData.transactions)
        .completed()
        .sort(sortByCreatedAt)
        .reverse()
        .last();

      if (status === InvoiceStatus.Paid && lastTransaction) {
        invoiceData.invoice.paidAt = lastTransaction.createdAt;
      }
      Invoice.updateStatus(
        invoiceData.invoice,
        status,
        invoiceData.invoice.createdAt
      );
      return invoiceData;
    });
  }

  /**
   * Memoised wrapper around `resolveExactStaffLocation`. The same provider
   * code repeats across many charges/payments, so caching avoids an N+1
   * lookup pattern over the same translation map.
   */
  private async _resolvePracticeForProvider(
    providerCode: string,
    translationMap: TranslationMapHandler,
    migrationPractices: INamedDocument<IPractice>[]
  ): Promise<WithRef<IPractice> | undefined> {
    if (this._practiceCache.has(providerCode)) {
      return this._practiceCache.get(providerCode);
    }
    const practice = await resolveExactStaffLocation(
      providerCode,
      translationMap,
      this.staffToPractice,
      migrationPractices
    );
    this._practiceCache.set(providerCode, practice);
    return practice;
  }

  private async _buildInvoiceFromCharges(
    invoiceCharges: IExactV2Transaction[],
    patient: WithRef<IPatient>,
    translationMap: TranslationMapHandler,
    migrationPractices: INamedDocument<IPractice>[]
  ): Promise<IInvoiceBuildData | undefined> {
    const firstCharge = first(invoiceCharges);
    if (!firstCharge) {
      return;
    }

    const practice = await this._resolvePracticeForProvider(
      firstCharge.providerCode ?? '',
      translationMap,
      migrationPractices
    );
    if (!practice) {
      throw new Error(
        `Failed to resolve practice for provider: ${firstCharge.providerCode}`
      );
    }

    const earliestChargeDate =
      minBy(invoiceCharges, (charge) =>
        toMomentTz(charge.date, practice.settings.timezone).valueOf()
      )?.date ?? firstCharge.date;
    const date = toMomentTz(earliestChargeDate, practice.settings.timezone);

    const invoice = Invoice.init({
      due: toTimestamp(date),
      practice: toNamedDocument(practice),
      from: toAccountDetails(practice),
      to: toAccountDetails(patient),
      reference: firstCharge.reference ?? '',
      issuedAt: toTimestamp(date),
      createdAt: toTimestamp(date),
    });

    const lineItems = await this._lineItems.build(
      invoiceCharges,
      translationMap
    );

    const { payments, claims, unallocated } =
      this._settlement.settle(invoiceCharges);
    const transactions = [
      ...(await this._buildPaymentTransactions(
        payments,
        practice,
        translationMap,
        migrationPractices
      )),
      ...claims.map((claim) => this._buildClaimTransaction(claim, practice)),
      ...unallocated.map((residual) =>
        this._buildSettledBalanceTransaction(residual, practice)
      ),
    ];

    /**
     * Pull the invoice date back to the earliest transaction so it never
     * appears after its own payments (e.g. batch-settled ACC receipts).
     */
    const earliestTransactionTimestamp = minBy(transactions, (transaction) =>
      toMomentTz(transaction.createdAt, practice.settings.timezone).valueOf()
    )?.createdAt;
    const invoiceTimestamp =
      earliestTransactionTimestamp &&
      toMomentTz(
        earliestTransactionTimestamp,
        practice.settings.timezone
      ).isBefore(date)
        ? earliestTransactionTimestamp
        : toTimestamp(date);

    return {
      invoice: {
        ...invoice,
        due: invoiceTimestamp,
        issuedAt: invoiceTimestamp,
        createdAt: invoiceTimestamp,
        items: lineItems,
        uid: ExactInvoiceBuilderV2._invoiceKey(firstCharge),
      },
      transactions,
    };
  }

  private async _buildPaymentTransactions(
    payments: IExactPaymentSettlement[],
    practice: WithRef<IPractice>,
    translationMap: TranslationMapHandler,
    migrationPractices: INamedDocument<IPractice>[]
  ): Promise<(IIdentifiable & ITransaction)[]> {
    return resolveSequentially(payments, async ({ paymentRow, amount }) => {
      const resolvedPractice = await this._resolvePracticeForProvider(
        paymentRow.providerCode ?? '',
        translationMap,
        migrationPractices
      );
      const effectivePractice = resolvedPractice ?? practice;

      const { type, provider, manualType } =
        this._labels.resolveMeta(paymentRow);
      const extendedData: IManualExtendedData = { transactionType: manualType };

      return {
        ...Transaction.init({
          ...Transaction.internalReference(provider, paymentRow.id),
          reference: paymentRow.reference ?? paymentRow.id,
          type,
          status: TransactionStatus.Complete,
          from: '',
          to: '',
          amount: roundTo2Decimals(amount),
          extendedData,
          createdAt: toTimestamp(
            toMomentTz(paymentRow.date, effectivePractice.settings.timezone)
          ),
          practiceRef: effectivePractice.ref,
          description: this._labels.describe(paymentRow),
        }),
      };
    });
  }

  /** Pending, so the ledger shows money in flight rather than patient debt. */
  private _buildClaimTransaction(
    { charge, amount }: IExactChargeAmount,
    practice: WithRef<IPractice>
  ): IIdentifiable & ITransaction {
    const payor = this._labels.payor(charge.payorId);
    const extendedData: IManualExtendedData = {
      transactionType: payor.manualType,
    };

    return {
      ...Transaction.init({
        ...Transaction.internalReference(
          TransactionProvider.Manual,
          `${charge.id}${PENDING_CLAIM_UID_SUFFIX}`
        ),
        type: TransactionType.Incoming,
        status: TransactionStatus.Pending,
        from: '',
        to: '',
        amount,
        extendedData,
        createdAt: toTimestamp(
          toMomentTz(charge.date, practice.settings.timezone)
        ),
        practiceRef: practice.ref,
        description: `${payor.name} claim (migrated)`,
      }),
    };
  }

  /** Named after Exact's balance: no source row says who supplied the money. */
  private _buildSettledBalanceTransaction(
    { charge, amount }: IExactChargeAmount,
    practice: WithRef<IPractice>
  ): IIdentifiable & ITransaction {
    return {
      ...Transaction.init({
        ...Transaction.internalReference(
          TransactionProvider.Manual,
          `${charge.id}${SETTLED_BALANCE_UID_SUFFIX}`
        ),
        type:
          charge.amount > 0
            ? TransactionType.Incoming
            : TransactionType.Outgoing,
        status: TransactionStatus.Complete,
        from: '',
        to: '',
        amount,
        createdAt: toTimestamp(
          toMomentTz(charge.date, practice.settings.timezone)
        ),
        practiceRef: practice.ref,
        description: 'Exact settled balance (migrated)',
      }),
    };
  }

  private async _buildDepositInvoices(
    patient: WithRef<IPatient>,
    depositTransactions: IExactV2Transaction[],
    migrationPractices: INamedDocument<IPractice>[],
    translationMap: TranslationMapHandler
  ): Promise<IInvoiceBuildData[]> {
    const grouped = groupBy(depositTransactions, (transaction) =>
      toISODate(transaction.date)
    );

    const results = await resolveSequentially(
      Object.values(grouped),
      async (group) =>
        this._buildDepositInvoice(
          patient,
          group,
          migrationPractices,
          translationMap
        )
    );

    return compact(results);
  }

  private async _buildDepositInvoice(
    patient: WithRef<IPatient>,
    group: IExactV2Transaction[],
    migrationPractices: INamedDocument<IPractice>[],
    translationMap: TranslationMapHandler
  ): Promise<IInvoiceBuildData | undefined> {
    const firstTransaction = first(group);
    if (!firstTransaction) {
      return;
    }

    const practice = await this._resolvePracticeForProvider(
      firstTransaction.providerCode ?? '',
      translationMap,
      migrationPractices
    );
    if (!practice) {
      throw new Error(
        `Failed to resolve practice for deposit transaction: ${firstTransaction.id}`
      );
    }

    const createdAt = toTimestamp(
      toMomentTz(firstTransaction.date, practice.settings.timezone)
    );

    const transactions: (IIdentifiable & ITransaction)[] = group.map(
      (transaction) => ({
        ...Transaction.init({
          ...Transaction.internalReference(
            TransactionProvider.Manual,
            transaction.id
          ),
          reference: transaction.reference ?? transaction.id,
          type: TransactionType.Incoming,
          status: TransactionStatus.Complete,
          from: '',
          to: '',
          amount: Math.abs(transaction.amount),
          createdAt,
          practiceRef: practice.ref,
          description: this._labels.describe(transaction),
        }),
      })
    );

    const items = group.map((transaction) => ({
      uid: transaction.id,
      type: InvoiceLineItemType.Deposit,
      description: transaction.reference ?? `Deposit ${transaction.id}`,
      amount: Math.abs(transaction.amount),
      tax: 0,
      taxStatus: TaxStrategy.GSTApplicable,
      quantity: 1,
    }));

    const invoice = Invoice.init({
      due: createdAt,
      practice: toNamedDocument(practice),
      from: toAccountDetails(practice),
      to: toAccountDetails(patient),
      reference: firstTransaction.reference ?? '',
      issuedAt: createdAt,
      createdAt,
      items,
    });

    return {
      invoice: {
        ...invoice,
        uid: firstTransaction.depositId ?? firstTransaction.id,
      },
      transactions,
    };
  }

  private _getInvoiceStatus(amountRemaining: number): InvoiceStatus {
    if (amountRemaining <= 0) {
      return InvoiceStatus.Paid;
    }
    return InvoiceStatus.Issued;
  }
}
